import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Chart } from 'angular-highcharts';
import { CH_ONBOARDING_DOCUMENT_TYPES } from 'src/app/modules/Cargo_Heating_modules/shared/ch-section-document/ch-onboarding-document.types';
import {
  OnboardingDetailSection,
  OnboardingSectionSavePayload,
  pickField,
} from '../onboarding-vessel-mapper.util';

export interface MainBoilerData {
  boiler_manufacturer?: string;
  boiler_capacity_kg_h?: number;
  boiler_count?: number;
  max_efficiency_pct?: number;
  steam_prod_at_max_efficiency_kg_h?: number;
  boiler_load_pct?: number;
  deck_steam_pressure_kg_cm2?: number;
  aux_boiler_fuel_consumption_kg_h?: number;
}

export interface CompositeBoilerData {
  composite_boiler_capacity_kg_h?: number;
  composite_boiler_fuel_consumption_kg_h?: number;
  steam_output_oil_fired_kg_h?: number;
  steam_output_me_exhaust_kg_h?: number;
  steam_output_ae_exhaust_kg_h?: number;
  deck_heater_capacity_kw?: number;
  ae_additional_consumption_mt?: number;
}

export interface EgbEconomizerData {
  steam_output_capacity_kg_h?: number;
  exhaust_gas_flow_rate_kg_h?: number;
  inlet_gas_temperature_c?: number;
  outlet_gas_temperature_c?: number;
}

export interface EngineRoomLineData {
  inside_diameter_mm?: number;
  outside_diameter_mm?: number;
  length_m?: number;
}

export interface MainLineSegmentData {
  segmentKey: string;
  inside_diameter_mm?: number;
  outside_diameter_mm?: number;
  length_m?: number;
}

export interface BranchLineGroupData {
  inside_diameter_mm?: number;
  outside_diameter_mm?: number;
  total_length_m?: number;
  line_count?: number;
}

export interface HeatingTankRow {
  tankKey: string;
  tank_volume_98_m3?: number;
  heating_coil_actual_length_m?: number;
  heating_coil_required_length_m?: number;
  length_per_group_m?: number;
  no_of_groups?: number;
  heating_ratio_m2_m3?: number;
}

export interface CapacityPlanRow {
  tankKey: string;
  frame_min?: number;
  frame_max?: number;
  volume_100_m3?: number;
  weight_mt?: number;
  vcg_from_bl_m?: number;
  lcg_from_midship_m?: number;
  tcg_from_cl_m?: number;
  i_max_m4?: number;
}

export interface CurvePoint {
  index?: number;
  steamProduction: number;
  thermalEfficiency: number;
}

@Component({
  selector: 'app-cargo-heating-detail',
  templateUrl: './cargo-heating-detail.component.html',
  styleUrls: ['./cargo-heating-detail.component.scss'],
})
export class CargoHeatingDetailComponent implements OnInit, OnChanges {
  @Input() vesselName = '';
  @Input() selectedImo = '';
  @Input() detail: Record<string, unknown> | null = null;
  @Input() loading = false;
  @Input() isDarkMode = false;
  @Input() embedded = false;
  @Input() savingSectionId: string | null = null;
  @Input() sections: OnboardingDetailSection[] = [];
  @Output() back = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();
  @Output() sectionSave = new EventEmitter<OnboardingSectionSavePayload>();

  boilerEfficiencyChart: Chart | null = null;
  activeCurveView: 'chart' | 'table' = 'chart';
  activePipelineTab: 'all' | 'main' | 'branch' | 'engine' = 'all';
  heatingTableViewMode: 'cards' | 'table' = 'cards';
  capacityViewMode: 'cards' | 'table' = 'cards';
  efficiencyViewLayout: 'cards' | 'matrix' = 'cards';
  efficiencySearchTerm = '';
  selectedEfficiencyRange: 'all' | 'low' | 'mid' | 'high' | 'milestones' = 'all';
  readonly onboardingDocuments = Object.values(CH_ONBOARDING_DOCUMENT_TYPES);

  get filteredEfficiencyPoints(): Array<CurvePoint & { index: number }> {
    const pts = this.efficiencyPoints.map((p, idx) => ({ ...p, index: idx + 1 }));
    if (!pts.length) return [];

    let filtered = pts;

    // Filter by load range
    if (this.selectedEfficiencyRange === 'low') {
      filtered = filtered.filter((p) => p.steamProduction <= 15000);
    } else if (this.selectedEfficiencyRange === 'mid') {
      filtered = filtered.filter(
        (p) => p.steamProduction > 15000 && p.steamProduction <= 25000,
      );
    } else if (this.selectedEfficiencyRange === 'high') {
      filtered = filtered.filter((p) => p.steamProduction > 25000);
    } else if (this.selectedEfficiencyRange === 'milestones') {
      const peakIdx = pts.findIndex(
        (p) => p.thermalEfficiency === this.peakEfficiency,
      );
      if (peakIdx !== -1) {
        filtered = filtered.filter((p, i) => Math.abs(i - peakIdx) <= 2);
      } else {
        filtered = filtered.filter(
          (p) => p.thermalEfficiency === this.peakEfficiency,
        );
      }
    }

    // Filter by search query
    const q = (this.efficiencySearchTerm || '').trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((p) => {
        return (
          String(p.index).includes(q) ||
          String(p.steamProduction).includes(q) ||
          String(p.thermalEfficiency).includes(q) ||
          p.thermalEfficiency.toFixed(2).includes(q)
        );
      });
    }

    return filtered;
  }

  get avgEfficiency(): number {
    const pts = this.efficiencyPoints;
    if (!pts.length) return 0;
    const sum = pts.reduce((acc, p) => acc + p.thermalEfficiency, 0);
    return sum / pts.length;
  }

  getEfficiencyCategory(efficiency: number): { label: string; class: string; icon: string } {
    if (efficiency >= 88.0) {
      return { label: 'Optimal', class: 'eff-optimal', icon: 'verified' };
    }
    if (efficiency >= 85.0) {
      return { label: 'High', class: 'eff-good', icon: 'trending_up' };
    }
    return { label: 'Moderate', class: 'eff-standard', icon: 'speed' };
  }

  getEfficiencyProgressWidth(efficiency: number): number {
    return Math.max(8, Math.min(100, ((efficiency - 80) / 10) * 100));
  }

  getTankPositionClass(tankKey: string): string {
    const upper = (tankKey || '').toUpperCase();
    if (upper.includes('PORT') || upper.endsWith('-P')) return 'pos-port';
    if (upper.includes('STBD') || upper.includes('STARBOARD') || upper.endsWith('-S')) return 'pos-stbd';
    if (upper.includes('CENTER') || upper.includes('CTR') || upper.endsWith('-C')) return 'pos-center';
    return 'pos-default';
  }

  getTankPositionBadge(tankKey: string): { label: string; class: string; icon: string } {
    const upper = (tankKey || '').toUpperCase();
    if (upper.includes('PORT') || upper.endsWith('-P')) {
      return { label: 'PORT', class: 'badge-port', icon: 'west' };
    }
    if (upper.includes('STBD') || upper.includes('STARBOARD') || upper.endsWith('-S')) {
      return { label: 'STBD', class: 'badge-stbd', icon: 'east' };
    }
    if (upper.includes('CENTER') || upper.includes('CTR') || upper.endsWith('-C')) {
      return { label: 'CENTER', class: 'badge-center', icon: 'adjust' };
    }
    return { label: 'TANK', class: 'badge-default', icon: 'view_in_ar' };
  }

  getCoilCoveragePct(actual?: number, required?: number): number {
    if (!actual || !required || required <= 0) return 0;
    return Math.min(100, Math.round((actual / required) * 100));
  }

  get totalVolume98(): number {
    return this.heatingTableRows.reduce((sum, t) => sum + (t.tank_volume_98_m3 || 0), 0);
  }

  get totalCapacityVolume(): number {
    return this.capacityPlanRows.reduce((sum, c) => sum + (c.volume_100_m3 || 0), 0);
  }

  get totalCapacityWeight(): number {
    return this.capacityPlanRows.reduce((sum, c) => sum + (c.weight_mt || 0), 0);
  }

  get avgHeatingRatio(): number {
    const list = this.heatingTableRows.filter(
      (t) => t.heating_ratio_m2_m3 !== undefined && t.heating_ratio_m2_m3 !== null,
    );
    if (!list.length) return 0;
    return (
      list.reduce((sum, t) => sum + (t.heating_ratio_m2_m3 || 0), 0) / list.length
    );
  }

  get totalActualCoilLength(): number {
    return this.heatingTableRows.reduce(
      (sum, t) => sum + (t.heating_coil_actual_length_m || 0),
      0,
    );
  }

  setCurveView(view: 'chart' | 'table'): void {
    this.activeCurveView = view;
    if (view === 'chart') {
      setTimeout(() => {
        if (!this.boilerEfficiencyChart) {
          this.buildEfficiencyChart();
        } else {
          try {
            this.boilerEfficiencyChart.ref?.reflow();
          } catch (_) {
            this.buildEfficiencyChart();
          }
        }
      }, 50);
    }
  }

  ngOnInit(): void {
    this.buildEfficiencyChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['detail'] || changes['isDarkMode']) {
      this.buildEfficiencyChart();
    }
  }

  // --- ROOT VESSEL IDENTITY HELPERS ---

  get resolvedVesselName(): string {
    return (
      pickField(this.detail, 'vessel_name', 'vesselName', 'ship') ||
      this.vesselName ||
      '—'
    );
  }

  get resolvedImo(): string {
    return (
      pickField(this.detail, 'vessel_imo', 'imo', 'imo_number') ||
      this.selectedImo ||
      '—'
    );
  }

  get segment(): string {
    return pickField(this.detail, 'segment', 'vessel_segment');
  }

  get tankerType(): string {
    return pickField(this.detail, 'tanker_type', 'vessel_type', 'tankerType');
  }

  get heatingSetup(): string {
    return pickField(
      this.detail,
      'heating_setup',
      'heating_arrangement',
      'heatingSetup',
      'heatingArrangement',
    );
  }

  get tankCount(): string {
    const direct = pickField(this.detail, 'tank_count', 'number_of_tanks', 'tankCount');
    if (direct !== '—') {
      return direct;
    }
    const ht = this.heatingTableRows;
    if (ht.length > 0) {
      return String(ht.length);
    }
    const cp = this.capacityPlanRows;
    if (cp.length > 0) {
      return String(cp.length);
    }
    return '—';
  }

  get steamPipesInsulation(): boolean {
    const val = this.detail?.['steam_pipes_insulation'];
    return (
      val === true ||
      val === 'true' ||
      val === 'Yes' ||
      val === 'yes' ||
      val === '1'
    );
  }

  get egcsScrubber(): boolean {
    const val = this.detail?.['egcs_scrubber'];
    return (
      val === true ||
      val === 'true' ||
      val === 'Yes' ||
      val === 'yes' ||
      val === '1'
    );
  }

  get creationTimeFormatted(): string {
    const val = pickField(this.detail, 'creation_time', 'created_at', 'submitted_at');
    return this.formatTimestamp(val);
  }

  get modificationTimeFormatted(): string {
    const val = pickField(this.detail, 'modification_time', 'updated_at', 'last_updated');
    return this.formatTimestamp(val);
  }

  private formatTimestamp(val: string): string {
    if (!val || val === '—') {
      return '—';
    }
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) {
        return val;
      }
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return val;
    }
  }

  // --- MAIN BOILER DATA ---

  get mainBoiler(): MainBoilerData | null {
    if (!this.detail) return null;
    const mb = (this.detail['main_boiler'] ||
      (this.detail['onboarding_data'] as any)?.main_boiler ||
      (this.detail['cargo_heating'] as any)?.main_boiler) as Record<string, unknown>;

    if (mb && typeof mb === 'object') {
      return {
        boiler_manufacturer: String(
          mb['boiler_manufacturer'] ??
            mb['boiler_make'] ??
            this.detail['boiler_make'] ??
            '—',
        ),
        boiler_capacity_kg_h: this.toNumber(
          mb['boiler_capacity_kg_h'] ??
            mb['boiler_capacity'] ??
            this.detail['boiler_capacity'],
        ),
        boiler_count: this.toNumber(
          mb['boiler_count'] ??
            mb['number_of_boilers'] ??
            this.detail['number_of_boilers'],
        ),
        max_efficiency_pct: this.toNumber(
          mb['max_efficiency_pct'] ??
            mb['max_efficiency'] ??
            this.detail['max_efficiency_percent'] ??
            this.peakEfficiency,
        ),
        steam_prod_at_max_efficiency_kg_h: this.toNumber(
          mb['steam_prod_at_max_efficiency_kg_h'] ??
            mb['steam_production_kg_h'] ??
            this.detail['steam_production_kg_h'],
        ),
        boiler_load_pct: this.toNumber(
          mb['boiler_load_pct'] ??
            mb['boiler_load_percentage'] ??
            this.detail['boiler_load_percentage'],
        ),
        deck_steam_pressure_kg_cm2: this.toNumber(
          mb['deck_steam_pressure_kg_cm2'] ??
            mb['deck_steam_pressure'] ??
            this.detail['deck_steam_pressure'],
        ),
        aux_boiler_fuel_consumption_kg_h: this.toNumber(
          mb['aux_boiler_fuel_consumption_kg_h'] ??
            this.detail['aux_boiler_fuel_consumption_kg_h'],
        ),
      };
    }

    // Fallback if flat properties exist
    return {
      boiler_manufacturer: String(this.detail['boiler_make'] ?? '—'),
      boiler_capacity_kg_h: this.toNumber(this.detail['boiler_capacity']),
      boiler_count: this.toNumber(this.detail['number_of_boilers']),
      max_efficiency_pct: this.toNumber(
        this.detail['max_efficiency_percent'] ?? this.peakEfficiency,
      ),
      steam_prod_at_max_efficiency_kg_h: this.toNumber(
        this.detail['steam_production_kg_h'],
      ),
      boiler_load_pct: this.toNumber(this.detail['boiler_load_percentage']),
      deck_steam_pressure_kg_cm2: this.toNumber(
        this.detail['deck_steam_pressure'],
      ),
      aux_boiler_fuel_consumption_kg_h: this.toNumber(
        this.detail['aux_boiler_fuel_consumption_kg_h'],
      ),
    };
  }

  // --- COMPOSITE BOILER & ECONOMIZER DATA ---

  get compositeBoiler(): CompositeBoilerData | null {
    if (!this.detail) return null;
    const cb = (this.detail['composite_boiler'] ||
      (this.detail['onboarding_data'] as any)?.composite_boiler) as Record<
      string,
      unknown
    >;

    if (cb && typeof cb === 'object') {
      return {
        composite_boiler_capacity_kg_h: this.toNumber(
          cb['composite_boiler_capacity_kg_h'] ??
            cb['capacity'] ??
            this.detail['composite_boiler_capacity_kg_h'],
        ),
        composite_boiler_fuel_consumption_kg_h: this.toNumber(
          cb['composite_boiler_fuel_consumption_kg_h'] ??
            this.detail['composite_boiler_fuel_consumption_kg_h'],
        ),
        steam_output_oil_fired_kg_h: this.toNumber(
          cb['steam_output_oil_fired_kg_h'] ??
            this.detail['steam_output_oil_fired_section_kg_h'],
        ),
        steam_output_me_exhaust_kg_h: this.toNumber(
          cb['steam_output_me_exhaust_kg_h'] ??
            this.detail['steam_output_exhaust_section_me_motor_kg_h'],
        ),
        steam_output_ae_exhaust_kg_h: this.toNumber(
          cb['steam_output_ae_exhaust_kg_h'] ??
            this.detail['steam_output_exhaust_section_ae_motor_kg_h'],
        ),
        deck_heater_capacity_kw: this.toNumber(
          cb['deck_heater_capacity_kw'] ??
            this.detail['deck_heater_capacity_kw'],
        ),
        ae_additional_consumption_mt: this.toNumber(
          cb['ae_additional_consumption_mt'] ??
            this.detail['ae_boiler_consumption_mt'],
        ),
      };
    }

    return {
      composite_boiler_capacity_kg_h: this.toNumber(
        this.detail['composite_boiler_capacity_kg_h'],
      ),
      composite_boiler_fuel_consumption_kg_h: this.toNumber(
        this.detail['composite_boiler_fuel_consumption_kg_h'],
      ),
      steam_output_oil_fired_kg_h: this.toNumber(
        this.detail['steam_output_oil_fired_section_kg_h'],
      ),
      steam_output_me_exhaust_kg_h: this.toNumber(
        this.detail['steam_output_exhaust_section_me_motor_kg_h'],
      ),
      steam_output_ae_exhaust_kg_h: this.toNumber(
        this.detail['steam_output_exhaust_section_ae_motor_kg_h'],
      ),
      deck_heater_capacity_kw: this.toNumber(
        this.detail['deck_heater_capacity_kw'],
      ),
      ae_additional_consumption_mt: this.toNumber(
        this.detail['ae_boiler_consumption_mt'],
      ),
    };
  }

  get egbEconomizer(): EgbEconomizerData | null {
    if (!this.detail) return null;
    const egb = (this.detail['egb_economizer'] ||
      (this.detail['onboarding_data'] as any)?.egb_economizer) as Record<
      string,
      unknown
    >;

    if (egb && typeof egb === 'object') {
      return {
        steam_output_capacity_kg_h: this.toNumber(
          egb['steam_output_capacity_kg_h'] ??
            egb['egb_capacity_kg_h'] ??
            this.detail['egb_capacity_kg_h'],
        ),
        exhaust_gas_flow_rate_kg_h: this.toNumber(
          egb['exhaust_gas_flow_rate_kg_h'] ??
            egb['egb_exhaust_gas_quantity_kg_h'] ??
            this.detail['egb_exhaust_gas_quantity_kg_h'],
        ),
        inlet_gas_temperature_c: this.toNumber(
          egb['inlet_gas_temperature_c'] ??
            egb['egb_gas_temp_inlet_deg_c'] ??
            this.detail['egb_gas_temp_inlet_deg_c'],
        ),
        outlet_gas_temperature_c: this.toNumber(
          egb['outlet_gas_temperature_c'] ??
            egb['egb_gas_temp_outlet_deg_c'] ??
            this.detail['egb_gas_temp_outlet_deg_c'],
        ),
      };
    }

    return {
      steam_output_capacity_kg_h: this.toNumber(
        this.detail['egb_capacity_kg_h'],
      ),
      exhaust_gas_flow_rate_kg_h: this.toNumber(
        this.detail['egb_exhaust_gas_quantity_kg_h'],
      ),
      inlet_gas_temperature_c: this.toNumber(
        this.detail['egb_gas_temp_inlet_deg_c'],
      ),
      outlet_gas_temperature_c: this.toNumber(
        this.detail['egb_gas_temp_outlet_deg_c'],
      ),
    };
  }

  // --- PIPELINE CONFIGURATION DATA ---

  get engineRoomLine(): EngineRoomLineData | null {
    if (!this.detail) return null;
    const pipe = this.detail['pipeline_configuration'] as Record<
      string,
      unknown
    >;
    const er = pipe?.['engine_room'] as Record<string, unknown>;
    if (er && typeof er === 'object') {
      return {
        inside_diameter_mm: this.toNumber(er['inside_diameter_mm']),
        outside_diameter_mm: this.toNumber(er['outside_diameter_mm']),
        length_m: this.toNumber(er['length_m']),
      };
    }
    return {
      inside_diameter_mm: this.toNumber(
        this.detail['pipeline_in_engine_room_thickness_mm'],
      ),
      outside_diameter_mm: undefined,
      length_m: this.toNumber(this.detail['pipeline_in_engine_room_length_m']),
    };
  }

  get mainLineSegments(): MainLineSegmentData[] {
    if (!this.detail) return [];
    const pipe = this.detail['pipeline_configuration'] as Record<
      string,
      unknown
    >;
    const ml = pipe?.['main_line'];

    if (ml && typeof ml === 'object' && !Array.isArray(ml)) {
      return Object.keys(ml).map((key) => {
        const item = (ml as Record<string, any>)[key] || {};
        return {
          segmentKey: this.formatSegmentKey(key),
          inside_diameter_mm: this.toNumber(item.inside_diameter_mm),
          outside_diameter_mm: this.toNumber(item.outside_diameter_mm),
          length_m: this.toNumber(item.length_m),
        };
      });
    }

    if (Array.isArray(ml)) {
      return ml.map((item, i) => ({
        segmentKey: item.name || item.segment || `Zone ${i + 1}`,
        inside_diameter_mm: this.toNumber(item.inside_diameter_mm),
        outside_diameter_mm: this.toNumber(item.outside_diameter_mm),
        length_m: this.toNumber(item.length_m),
      }));
    }

    return [];
  }

  get totalMainLineLength(): number {
    return this.mainLineSegments.reduce(
      (sum, s) => sum + (s.length_m || 0),
      0,
    );
  }

  get branchLineGroups(): BranchLineGroupData[] {
    if (!this.detail) return [];
    const pipe = this.detail['pipeline_configuration'] as Record<
      string,
      unknown
    >;
    const bl = pipe?.['branch_line'];

    if (Array.isArray(bl)) {
      return bl.map((item) => ({
        inside_diameter_mm: this.toNumber(item.inside_diameter_mm),
        outside_diameter_mm: this.toNumber(item.outside_diameter_mm),
        total_length_m: this.toNumber(item.total_length_m ?? item.length_m),
        line_count: this.toNumber(item.line_count ?? item.count),
      }));
    }

    return [];
  }

  get totalBranchLineLength(): number {
    return this.branchLineGroups.reduce(
      (sum, b) => sum + (b.total_length_m || 0),
      0,
    );
  }

  get totalBranchLineCount(): number {
    return this.branchLineGroups.reduce(
      (sum, b) => sum + (b.line_count || 0),
      0,
    );
  }

  // --- TANK HEATING TABLE ---

  get heatingTableRows(): HeatingTankRow[] {
    if (!this.detail) return [];
    const ht = this.detail['heating_table'];

    if (ht && typeof ht === 'object' && !Array.isArray(ht)) {
      return Object.keys(ht).map((tankKey) => {
        const item = (ht as Record<string, any>)[tankKey] || {};
        return {
          tankKey,
          tank_volume_98_m3: this.toNumber(item.tank_volume_98_m3 ?? item.volume),
          heating_coil_actual_length_m: this.toNumber(
            item.heating_coil_actual_length_m ?? item.actual_length,
          ),
          heating_coil_required_length_m: this.toNumber(
            item.heating_coil_required_length_m ?? item.required_length,
          ),
          length_per_group_m: this.toNumber(
            item.length_per_group_m ?? item.group_length,
          ),
          no_of_groups: this.toNumber(item.no_of_groups ?? item.groups_count),
          heating_ratio_m2_m3: this.toNumber(
            item.heating_ratio_m2_m3 ?? item.ratio,
          ),
        };
      });
    }

    if (Array.isArray(ht)) {
      return ht.map((item, i) => ({
        tankKey: item.name || item.tank || item.tank_name || `Tank ${i + 1}`,
        tank_volume_98_m3: this.toNumber(item.tank_volume_98_m3 ?? item.volume),
        heating_coil_actual_length_m: this.toNumber(
          item.heating_coil_actual_length_m ?? item.actual_length,
        ),
        heating_coil_required_length_m: this.toNumber(
          item.heating_coil_required_length_m ?? item.required_length,
        ),
        length_per_group_m: this.toNumber(
          item.length_per_group_m ?? item.group_length,
        ),
        no_of_groups: this.toNumber(item.no_of_groups ?? item.groups_count),
        heating_ratio_m2_m3: this.toNumber(
          item.heating_ratio_m2_m3 ?? item.ratio,
        ),
      }));
    }

    return [];
  }

  // --- CAPACITY PLAN TABLE ---

  get capacityPlanRows(): CapacityPlanRow[] {
    if (!this.detail) return [];
    const cp = this.detail['capacity_plan'];

    if (cp && typeof cp === 'object' && !Array.isArray(cp)) {
      return Object.keys(cp).map((tankKey) => {
        const item = (cp as Record<string, any>)[tankKey] || {};
        return {
          tankKey,
          frame_min: this.toNumber(item.frame_min),
          frame_max: this.toNumber(item.frame_max),
          volume_100_m3: this.toNumber(item.volume_100_m3 ?? item.volume),
          weight_mt: this.toNumber(item.weight_mt ?? item.weight),
          vcg_from_bl_m: this.toNumber(item.vcg_from_bl_m ?? item.vcg),
          lcg_from_midship_m: this.toNumber(item.lcg_from_midship_m ?? item.lcg),
          tcg_from_cl_m: this.toNumber(item.tcg_from_cl_m ?? item.tcg),
          i_max_m4: this.toNumber(item.i_max_m4 ?? item.i_max),
        };
      });
    }

    if (Array.isArray(cp)) {
      return cp.map((item, i) => ({
        tankKey: item.name || item.tank || item.tank_name || `Tank ${i + 1}`,
        frame_min: this.toNumber(item.frame_min),
        frame_max: this.toNumber(item.frame_max),
        volume_100_m3: this.toNumber(item.volume_100_m3 ?? item.volume),
        weight_mt: this.toNumber(item.weight_mt ?? item.weight),
        vcg_from_bl_m: this.toNumber(item.vcg_from_bl_m ?? item.vcg),
        lcg_from_midship_m: this.toNumber(item.lcg_from_midship_m ?? item.lcg),
        tcg_from_cl_m: this.toNumber(item.tcg_from_cl_m ?? item.tcg),
        i_max_m4: this.toNumber(item.i_max_m4 ?? item.i_max),
      }));
    }

    return [];
  }

  // --- BOILER EFFICIENCY CURVE DATA & METRICS ---

  get efficiencyPoints(): CurvePoint[] {
    if (!this.detail) return [];
    const bec = this.detail['boiler_efficiency_curve'] as Record<
      string,
      unknown
    >;
    const rawPoints = (bec?.['points'] ??
      (bec?.['curve'] as any)?.points ??
      []) as any[];

    if (!Array.isArray(rawPoints)) return [];

    return rawPoints
      .map((p, idx) => ({
        index: idx + 1,
        steamProduction: Number(p.steamProduction ?? p.steam_production ?? p.x ?? 0),
        thermalEfficiency: Number(
          p.thermalEfficiency ?? p.thermal_efficiency ?? p.efficiency ?? p.y ?? 0,
        ),
      }))
      .filter((p) => !isNaN(p.steamProduction) && !isNaN(p.thermalEfficiency));
  }

  get peakEfficiencyPoint(): CurvePoint | null {
    const pts = this.efficiencyPoints;
    if (!pts.length) return null;
    return pts.reduce(
      (max, p) => (p.thermalEfficiency > max.thermalEfficiency ? p : max),
      pts[0],
    );
  }

  get peakEfficiency(): number {
    return this.peakEfficiencyPoint?.thermalEfficiency ?? 0;
  }

  get minEfficiencyPoint(): CurvePoint | null {
    const pts = this.efficiencyPoints;
    if (!pts.length) return null;
    return pts.reduce(
      (min, p) => (p.thermalEfficiency < min.thermalEfficiency ? p : min),
      pts[0],
    );
  }

  get minSteamProduction(): number {
    const pts = this.efficiencyPoints;
    if (!pts.length) return 0;
    return pts[0].steamProduction;
  }

  get maxSteamProduction(): number {
    const pts = this.efficiencyPoints;
    if (!pts.length) return 0;
    return pts[pts.length - 1].steamProduction;
  }

  private buildEfficiencyChart(): void {
    const pts = this.efficiencyPoints;
    if (!pts.length) {
      this.boilerEfficiencyChart = null;
      return;
    }

    const isDark = this.isDarkMode;
    const seriesData = pts.map((p) => [p.steamProduction, p.thermalEfficiency]);

    const yMin = Math.floor(
      Math.min(...pts.map((p) => p.thermalEfficiency)) - 1,
    );
    const yMax = Math.ceil(
      Math.max(...pts.map((p) => p.thermalEfficiency)) + 1,
    );

    this.boilerEfficiencyChart = new Chart({
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height: 340,
        spacing: [20, 20, 20, 10],
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        title: {
          text: 'Steam Production (kg/h)',
          style: {
            color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '12px',
            fontWeight: '600',
          },
        },
        labels: {
          style: {
            color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '11px',
          },
          formatter: function () {
            return (Number(this.value) / 1000).toFixed(0) + 'k';
          },
        },
        gridLineColor: isDark
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0, 0, 0, 0.06)',
        lineColor: isDark
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)',
        tickColor: isDark
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.2)',
      },
      yAxis: {
        title: {
          text: 'Thermal Efficiency (%)',
          style: {
            color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '12px',
            fontWeight: '600',
          },
        },
        labels: {
          style: {
            color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '11px',
          },
          formatter: function () {
            return this.value + '%';
          },
        },
        min: yMin > 0 ? yMin : 80,
        max: yMax <= 100 ? yMax : 100,
        gridLineColor: isDark
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0, 0, 0, 0.06)',
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderRadius: 8,
        style: {
          color: isDark ? '#f8fafc' : '#0f172a',
          fontSize: '12px',
        },
        formatter: function () {
          const xVal = Number(this.x).toLocaleString();
          const yVal = Number(this.y).toFixed(2);
          return `
            <div style="padding: 4px 6px;">
              <div style="font-weight: 700; color: #38bdf8; margin-bottom: 4px;">Thermal Efficiency</div>
              <div>Steam Output: <b>${xVal} kg/h</b></div>
              <div>Efficiency: <b>${yVal}%</b></div>
            </div>
          `;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        spline: {
          lineWidth: 3,
          color: '#38bdf8',
          shadow: {
            color: 'rgba(56, 189, 248, 0.35)',
            width: 8,
            offsetX: 0,
            offsetY: 2,
          },
          marker: {
            enabled: false,
            radius: 4,
            states: {
              hover: {
                enabled: true,
                radius: 6,
                fillColor: '#38bdf8',
                lineWidth: 2,
                lineColor: '#ffffff',
              },
            },
          },
        },
      },
      series: [
        {
          name: 'Thermal Efficiency',
          type: 'spline',
          data: seriesData,
        },
      ],
    });
  }

  // --- UTILITY HELPERS ---

  private toNumber(val: unknown): number | undefined {
    if (val === null || val === undefined || val === '') return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  }

  private formatSegmentKey(key: string): string {
    return key.replace(/_/g, ' ').toUpperCase();
  }

  formatNumber(val?: number, decimals = 1): string {
    if (val === null || val === undefined) return '—';
    return Number(val).toLocaleString('en-US', {
      maximumFractionDigits: decimals,
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
